import React, { useMemo, useState, Children } from 'react';
import { useAppStore } from '../../../../hooks/useAppStore';
import { PageLayout } from '../shared/PageLayout';
import { DataTable } from '../shared/DataTable';
import { SlideOver } from '../shared/SlideOver';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { StatCard } from '../shared/StatCard';
import { Avatar } from '../shared/Avatar';
import { MasteryBar } from '../shared/MasteryBar';
import { timeAgo } from '../shared/utils';
import { SearchIcon, UsersIcon, HeartIcon, TrendingUpIcon, Plus } from 'lucide-react';
import { Button } from '../../../ui/Button';
export function ParentsPage() {
  const {
    accounts,
    createAccount,
    getLearnerData
  } = useAppStore();
  // accounts is AccountsState (object) — extract flat array
  const allAccounts = useMemo(() => accounts?.accountsById ? Object.values(accounts.accountsById) : [], [accounts]);
  const [search, setSearch] = useState('');
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
  // Add Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newParentName, setNewParentName] = useState('');
  const [newParentEmail, setNewParentEmail] = useState('');
  const handleAddParent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newParentName.trim()) return;
    createAccount('parent', {
      displayName: newParentName,
      email: newParentEmail
    });
    setIsAddModalOpen(false);
    setNewParentName('');
    setNewParentEmail('');
  };
  const parents = useMemo(() => allAccounts.filter(a => a.role === 'parent'), [allAccounts]);
  const learners = useMemo(() => allAccounts.filter(a => a.role === 'learner'), [allAccounts]);
  // Link parents to children via familyId, parentId field, or relationships
  const enrichedParents = useMemo(() => {
    return parents.map(parent => {
      const children = learners.filter(l => (l as any).parentId === parent.id || (l as any).familyId && (l as any).familyId === (parent as any).familyId || accounts.relationships?.parentToLearners?.[parent.id]?.includes(l.id));
      return {
        ...parent,
        children,
        childrenCount: children.length,
        lastActive: parent.lastActive ?? 0
      };
    });
  }, [parents, learners, accounts.relationships]);
  const filtered = useMemo(() => {
    return enrichedParents.filter(p => !search || p.displayName.toLowerCase().includes(search.toLowerCase()) || p.children.some(c => c.displayName.toLowerCase().includes(search.toLowerCase())));
  }, [enrichedParents, search]);
  const selectedParent = useMemo(() => selectedParentId ? enrichedParents.find(p => p.id === selectedParentId) : null, [selectedParentId, enrichedParents]);
  const selectedChildrenDetail = useMemo(() => {
    if (!selectedParent) return [];
    return selectedParent.children.map(child => {
      const learnerData = getLearnerData(child.id);
      const familyMastery = learnerData?.familyMastery || {};
      const masteryValues = Object.values(familyMastery).map(m => m.masteryScore);
      const avgMastery = masteryValues.length > 0 ? Math.round(masteryValues.reduce((sum, val) => sum + val, 0) / masteryValues.length) : 0;
      const totalAttempts = Object.values(familyMastery).reduce((sum, m) => sum + m.attempts, 0);
      const sessionCount = Math.ceil(totalAttempts / 10);
      return {
        ...child,
        avgMastery,
        sessionCount
      };
    });
  }, [selectedParent, getLearnerData]);
  const columns = [{
    key: 'displayName',
    label: 'Naam',
    render: (v: string) => <div className="flex items-center gap-2.5" data-id="element-989">
          <Avatar name={v} data-id="element-990" />
          <span className="text-sm font-medium text-gray-900" data-id="element-991">{v}</span>
        </div>
  }, {
    key: 'childrenCount',
    label: 'Kinderen',
    render: (v: number, row: (typeof filtered)[0]) => <div data-id="element-992">
          <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-993">
            <HeartIcon className="w-3.5 h-3.5 text-rose-400" data-id="element-994" />
            {v}
          </span>
          {row.children.length > 0 && <div className="text-xs text-gray-400 mt-0.5" data-id="element-995">
              {row.children.map(c => c.displayName).join(', ')}
            </div>}
        </div>
  }, {
    key: 'lastActive',
    label: 'Laatste activiteit',
    render: (v: number) => <span className="text-xs text-gray-500" data-id="element-996">{timeAgo(v)}</span>
  }];
  return <PageLayout title="Ouders" description={`${filtered.length} van ${parents.length} ouders`} actions={<div className="flex gap-2" data-id="element-998">
          <ConnectionBadge status="live" data-id="element-999" />
          <Button size="sm" onClick={() => setIsAddModalOpen(true)} data-id="element-1000">
            <Plus className="w-4 h-4 mr-1.5" data-id="element-1001" />
            Ouder toevoegen
          </Button>
        </div>} data-id="element-997">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6" data-id="element-1002">
        <StatCard label="Totaal ouders" value={parents.length} icon={<UsersIcon className="w-5 h-5" data-id="element-1004" />} color="rose" data-id="element-1003" />
        <StatCard label="Gekoppelde kinderen" value={enrichedParents.reduce((sum, p) => sum + p.childrenCount, 0)} icon={<HeartIcon className="w-5 h-5" data-id="element-1006" />} color="pink" data-id="element-1005" />
        <StatCard label="Leerlingen totaal" value={learners.length} icon={<TrendingUpIcon className="w-5 h-5" data-id="element-1008" />} color="blue" data-id="element-1007" />
      </div>

      <div className="relative mb-4" data-id="element-1009">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1010" />
        <input type="text" placeholder="Zoek op naam of kind..." value={search} onChange={e => setSearch(e.target.value)} className="w-full max-w-sm pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1011" />
      </div>

      {filtered.length === 0 ? <div className="bg-white rounded-xl border border-gray-200 py-16 text-center" data-id="element-1012">
          <UsersIcon className="w-10 h-10 text-gray-200 mx-auto mb-3" data-id="element-1013" />
          <p className="text-sm text-gray-500" data-id="element-1014">Geen ouders gevonden</p>
          {parents.length === 0 && <p className="text-xs text-gray-400 mt-1" data-id="element-1015">
              Er zijn nog geen ouder-accounts aangemaakt
            </p>}
        </div> : <DataTable columns={columns} data={filtered} onRowClick={row => setSelectedParentId(row.id)} data-id="element-1016" />}

      <SlideOver open={!!selectedParent} onClose={() => setSelectedParentId(null)} title={selectedParent?.displayName ?? ''} data-id="element-1017">
        {selectedParent && <div className="space-y-6" data-id="element-1018">
            <div className="flex items-center gap-4 pb-4 border-b border-gray-100" data-id="element-1019">
              <Avatar name={selectedParent.displayName} size="md" data-id="element-1020" />
              <div data-id="element-1021">
                <div className="font-semibold text-gray-900" data-id="element-1022">
                  {selectedParent.displayName}
                </div>
                <div className="text-xs text-gray-400 mt-0.5" data-id="element-1023">
                  Laatste activiteit: {timeAgo(selectedParent.lastActive)}
                </div>
              </div>
            </div>

            {selectedChildrenDetail.length > 0 ? <div data-id="element-1024">
                <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-1025">
                  Kinderen ({selectedChildrenDetail.length})
                </h3>
                <div className="space-y-3" data-id="element-1026">
                  {selectedChildrenDetail.map(child => <div key={child.id} className="bg-gray-50 rounded-lg p-3" data-id="element-1027">
                      <div className="flex items-center justify-between mb-2" data-id="element-1028">
                        <div className="flex items-center gap-2" data-id="element-1029">
                          <Avatar name={child.displayName} data-id="element-1030" />
                          <span className="text-sm font-medium text-gray-800" data-id="element-1031">
                            {child.displayName}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500" data-id="element-1032">
                          {child.sessionCount} sessies
                        </span>
                      </div>
                      <MasteryBar value={child.avgMastery} data-id="element-1033" />
                    </div>)}
                </div>
              </div> : <div className="text-center py-8" data-id="element-1034">
                <HeartIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" data-id="element-1035" />
                <p className="text-sm text-gray-400" data-id="element-1036">
                  Geen gekoppelde kinderen gevonden
                </p>
                <p className="text-xs text-gray-300 mt-1" data-id="element-1037">
                  Koppeling via familyId of parentId
                </p>
              </div>}
          </div>}
      </SlideOver>

      {/* Add Parent Modal */}
      <SlideOver open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Nieuwe ouder" description="Voeg een ouderaccount toe" data-id="element-1038">
        <form onSubmit={handleAddParent} className="space-y-4" data-id="element-1039">
          <div data-id="element-1040">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1041">
              Naam
            </label>
            <input type="text" required value={newParentName} onChange={e => setNewParentName(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Bijv. Vader van Sophie" data-id="element-1042" />
          </div>

          <div data-id="element-1043">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1044">
              E-mailadres (optioneel)
            </label>
            <input type="email" value={newParentEmail} onChange={e => setNewParentEmail(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="ouder@voorbeeld.nl" data-id="element-1045" />
          </div>

          <div className="pt-4 flex justify-end gap-2" data-id="element-1046">
            <Button type="button" variant="secondary" onClick={() => setIsAddModalOpen(false)} data-id="element-1047">
              Annuleren
            </Button>
            <Button type="submit" data-id="element-1048">Aanmaken</Button>
          </div>
        </form>
      </SlideOver>
    </PageLayout>;
}